import { Dialog } from '@angular/cdk/dialog'
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  effect,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { DialogService } from '../../services/dialog.service'
import { ErrorService } from '../../services/error.service'
import { RouteService } from '../../services/route.service'
import { SpinnerService } from '../../services/spinner.service'
import { WindowService } from '../../services/window.service'
import { ApiError } from '../../types/apiError'
import {
  IngredientListElement,
  IngredientListFilter,
  IngredientListResult,
} from '../../types/ingredients'
import { ROUTES } from '../../constants/routes'
import { CommonModule } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { IngredientListComponent } from './ingredient-list/ingredient-list.component'
import { IngredientService } from '../../services/ingredient.service'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { Router } from '@angular/router'
import { USER_ROLES } from '../../constants/auth'
import { AuthService } from '../../services/auth.service'
import { DisallowQuotesDirective } from '../../directives/disallow-quotes.directive'
import { TranslationService } from '../../services/translation.service'

@Component({
  selector: 'app-ingredients',
  imports: [
    CommonModule,
    MatInputModule,
    MatFormField,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    FormsModule,
    MatIcon,
    IngredientListComponent,
    ReactiveFormsModule,
    DisallowQuotesDirective,
  ],
  templateUrl: './ingredients.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ingredients.component.scss',
})
export class IngredientsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator

  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  errorService = inject(ErrorService)
  spinnerService = inject(SpinnerService)
  windowService = inject(WindowService)
  ingredientService = inject(IngredientService)
  routeService = inject(RouteService)
  formBuilder = inject(FormBuilder)
  router = inject(Router)
  authService = inject(AuthService)
  translationService = inject(TranslationService)

  filterForm = this.formBuilder.group({
    name: [null],
    casNumber: [null],
  })

  isMobileView: boolean = false
  page: number = 1

  activeFilter: IngredientListFilter = {
    name: null,
    casNumber: null,
  }

  ingredients: IngredientListResult = {
    totalRecords: 0,
    results: [],
  }
  displayedColumns: Array<string> = [
    'name',
    'otherNames',
    'casNumbers',
    'actions',
  ]
  dataSource = new MatTableDataSource<IngredientListElement>(
    this.ingredients.results,
  )

  isAdmin: boolean = false
  isComplianceOfficer: boolean = false

  constructor() {
    // this acts as a subscription to the currentUserRole signal changes to update the flags below
    effect(() => {
      this.isAdmin = this.authService.currentUserRole() === USER_ROLES.ADMIN
      this.isComplianceOfficer =
        this.authService.currentUserRole() === USER_ROLES.COMPLIANCE_OFFICER
    })
  }

  async ngOnInit(): Promise<void> {
    try {
      this.spinnerService.startSpinner()
      this.windowService.isMobileView$.subscribe((isMobileView) => {
        this.isMobileView = isMobileView
      })

      const currentRoute = this.routeService.getUserRoutes(
        ROUTES.VITE_MARKETPLACE.title,
        ROUTES.INGREDIENTS.path,
      )
      this.routeService.setRoute(currentRoute)

      await this.getIngredientList()

      const currentUserRole = this.authService.currentUserRole()
      if (currentUserRole === '') {
        const session = this.authService.getSessionFromLS()
        if (session?.user.role) {
          this.authService.currentUserRole.set(session.user.role)
        } else {
          this.authService.currentUserRole.set('Guest')
        }
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  ngAfterViewInit(): void {
    // Assigns the paginator to table dataSource
    this.dataSource.paginator = this.paginator
  }

  /**
   * Handles the page change events from the paginator and gets the new page data from the API
   * @param {PageEvent} event Event containing new page
   */
  async changePage(event: PageEvent) {
    this.page = event.pageIndex + 1
    await this.getIngredientList()
  }

  /**
   * Makes request to get substance regulations
   */
  async getIngredientList() {
    try {
      if (!this.filterForm.valid) {
        this.filterForm.markAllAsTouched()
        return
      }
      this.spinnerService.startSpinner()
      const filter = this.filterForm.value as IngredientListFilter

      this.ingredients = await firstValueFrom(
        this.ingredientService.getIngredientList(this.page, filter),
      )

      this.onFilterChange()

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets page index on filter change
   */
  onFilterChange() {
    const filter = this.filterForm.value as IngredientListFilter
    for (const key of Object.keys(filter)) {
      let resetPage = false
      if (
        filter[key as keyof IngredientListFilter]?.toLowerCase() !=
        this.activeFilter[key as keyof IngredientListFilter]?.toLowerCase()
      ) {
        resetPage = true
      }

      if (resetPage) {
        this.activeFilter = JSON.parse(JSON.stringify(filter))
        this.page = 1
        if (this.paginator != null) {
          this.paginator.pageIndex = 0
        }
      }
    }
  }

  /**
   * Resets the filter values
   */
  resetFilter() {
    this.filterForm.reset()
  }

  /**
   * Routes user to Ingredient Create Screen
   */
  goToCreate() {
    this.router.navigate(['/ingredients/create'])
  }

  /**
   * Routes user to Ingredient Import Screen
   */
  goToImport() {
    this.router.navigate(['/ingredients/import'])
  }

  /**
   * Determines if the filter button should be disabled based on input values
   * @returns {boolean} Whether the filter button should be disabled
   */
  isFilterButtonDisabled(): boolean {
    const nameValue = this.filterForm.get('name')?.value as string | null
    const casNumberValue = this.filterForm.get('casNumber')?.value as
      string | null

    // If both inputs are empty, enable the button
    if (
      (!nameValue || nameValue === '') &&
      (!casNumberValue || casNumberValue === '')
    ) {
      return false
    }

    // If name has a value and it's less than 2 characters, disable the button
    if (nameValue && nameValue !== '' && nameValue.length < 2) {
      return true
    }

    // If casNumber has a value and it's less than 2 characters, disable the button
    if (casNumberValue && casNumberValue !== '' && casNumberValue.length < 2) {
      return true
    }

    // In all other cases, enable the button
    return false
  }
}
